<script setup>
// Плеєр для прямого ефіру з Mux. Свідомо не переюзує watch/Player.vue:
// той намертво зшитий з Strapi, списком серій, архівом чату, кешем серій
// та збереженням прогресу — на /live не потрібне нічого з цього, а тягнути
// його сюди означало б тягнути й половину сайту.
//
// Спільне з основним плеєром: логіка вибору hls.js vs нативний HLS,
// шейдер Anime4K (той самий ключ у localStorage, тож налаштування
// переносяться між сторінками) і мова оформлення контролів.

let Hls = null
let Anime4KJS = null

const props = defineProps({
  src: { type: String, required: true },
  chatOpen: { type: Boolean, default: false },
  // Коли стрім почався на Twitch (тягне сторінка) — з цього рахуємо, скільки
  // ефір уже йде. Лічильники глядачів плеєру не потрібні: вони живуть у
  // шапці сторінки поруч із назвою ефіру
  startedAt: { type: String, default: null },
  // Назва ефіру — потрібна лише як підпис на екрані телевізора, коли
  // трансляцію віддали на Chromecast
  title: { type: String, default: '' },
  // Ефір іде з нашого власного стрім-сервера, а не з Mux
  testMode: { type: Boolean, default: false },
  // Що саме розгортати на весь екран. Сторінка передає свій корінь, щоб
  // у фулскрін пішов і плеєр, і чат — інакше чат лишався б за кадром
  fullscreenTarget: { type: null, default: null },
  // Ширина чату у % екрана. На тачі її нічим не змінити — роздільник між
  // плеєром і чатом тягнеться мишею, тож на телефоні даємо повзунок у
  // налаштуваннях
  chatWidth: { type: Number, default: 26 }
})

// controls-visible віддаємо назовні, щоб сторінка могла ховати свою кнопку
// «Чат» разом із панеллю керування плеєра
const emit = defineEmits([
  'toggle-chat',
  // Зробити кліп: редактор живе на сторінці (live.vue), плеєр лише кличе
  'clip',
  'update:controlsVisible',
  'update:pseudoFullscreen',
  'update:chatWidth'
])

// Значення тримаємось у межах, які приймає сама панель чату (15–50% екрана)
const chatWidthOptions = [
  { value: 15, label: 'Вузький (15%)' },
  { value: 20, label: 'Компактний (20%)' },
  { value: 26, label: 'Звичайний (26%)' },
  { value: 33, label: 'Широкий (33%)' },
  { value: 40, label: 'Дуже широкий (40%)' },
  { value: 50, label: 'Половина екрана (50%)' }
]

const chatWidthProxy = computed({
  // Ширину могли підкрутити роздільником до значення поза списком — тоді
  // показуємо найближчий варіант, інакше select лишався б порожнім
  get: () =>
    chatWidthOptions.reduce((best, opt) =>
      Math.abs(opt.value - props.chatWidth) < Math.abs(best.value - props.chatWidth)
        ? opt
        : best
    ).value,
  set: (value) => emit('update:chatWidth', Number(value))
})

// --- Refs ---
const playerRef = ref(null)
const videoRef = ref(null)
const shaderCanvasRef = ref(null) // прихований канвас, куди рендерить бібліотека
const shaderDisplayCanvasRef = ref(null) // видимий канвас — його бачить глядач
const compareContainerRef = ref(null)
const hlsInstance = shallowRef(null)

// --- Стан відтворення ---
const playing = ref(false)
const waiting = ref(false)
const volume = ref(1)
const muted = ref(false)
const isMobile = ref(false)

// Стрім може бути просто не запущений — Mux віддає 404 на playback URL,
// поки live stream в стані idle. Це не помилка, а звичайний офлайн.
const streamOffline = ref(false)
const failed = ref(false)
const retryIn = ref(0)

// --- Тривалість ефіру ---
// Перемотки на /live немає свідомо: це прямий ефір, а не запис. Замість
// скрабера показуємо, скільки стрім уже йде — рахуємо від started_at,
// який віддає Twitch (а не від currentTime відео: той залежить від того,
// коли конкретний глядач відкрив сторінку).
const now = ref(Date.now())
let uptimeTimer = null

const uptime = computed(() => {
  if (!props.startedAt) return null
  const started = new Date(props.startedAt).getTime()
  if (!isFinite(started)) return null
  return Math.max(0, Math.floor((now.value - started) / 1000))
})

// --- Якість (рівні HLS) ---
const levels = ref([])
const currentLevel = ref(-1) // -1 = авто
// Підпис рівня, який реально грає (з fps, якщо він 60) — його показуємо
// в дужках біля «Авто»
const activeLevelLabel = ref('')
// Чи грає зараз рівень «тільки звук» — щоб не показувати глядачеві мило
// 256×144 на весь екран, а чесно накрити його заставкою
const activeLevelAudioOnly = ref(false)
// Рівні є на обох транспортах: hls.js віддає їх сам, а для нативного HLS
// (iPhone, iPad, Safari) ми розбираємо мастер-плейлист руками — див.
// loadNativeLevels нижче
const qualityControllable = computed(() => levels.value.length > 1)

// Через що зараз грає ефір. Нативний шлях відрізняється всім: рівень там
// перемикається підміною src, а не властивістю плеєра
const usingNativeHls = ref(false)
// Мастер-плейлист (він же «Авто» для нативного шляху): у props.src він теж
// лежить, але тримаємо окремо — так видно, куди вертатись із конкретного рівня
const nativeMasterSrc = ref('')

// --- Anime4K шейдер (порт з основного плеєра) ---
const shaderSupported = ref(false)
const shaderMode = ref('off')
const shaderQuality = ref('m')
const shaderScale = ref('2k')
const shaderActive = computed(
  () => shaderMode.value !== 'off' && shaderSupported.value
)
const videoUpscaler = shallowRef(null)

const shaderModeOptions = [
  { value: 'off', label: 'Вимкнено' },
  { value: 'restore', label: 'Restore (без апскейлу)' },
  { value: 'simple', label: 'Simple (апскейл x2)' },
  { value: 'lower-a', label: 'Lower-End A (апскейл x4)' },
  { value: 'lower-b', label: 'Lower-End B (апскейл x4)' },
  { value: 'lower-c', label: 'Lower-End C (апскейл x4)' },
  { value: 'higher-a', label: 'Higher-End A (апскейл x4)' },
  { value: 'higher-b', label: 'Higher-End B (апскейл x4)' },
  { value: 'higher-c', label: 'Higher-End C (апскейл x4)' }
]

const shaderQualityOptions = computed(() => {
  if (shaderMode.value === 'simple' || shaderMode.value === 'restore') {
    return [
      { value: 's', label: 'Швидкий' },
      { value: 'm', label: 'Середній' },
      { value: 'l', label: 'Великий' },
      { value: 'vl', label: 'Дуже великий' },
      { value: 'ul', label: 'Ультравеликий' }
    ]
  }
  if (shaderMode.value === 'off') return []
  return [
    { value: 'normal', label: 'Звичайна' },
    { value: 'fast', label: 'Швидка' }
  ]
})

const defaultShaderQuality = (mode) => {
  if (mode === 'simple' || mode === 'restore') return 'm'
  if (mode === 'off') return null
  return 'normal'
}

const shaderScaleOptions = [
  { value: 'native', label: 'Нативна' },
  { value: '2k', label: '2K' },
  { value: '4k', label: '4K' }
]

// --- Режим порівняння (split-screen: оригінал | апскейл) ---
const compareMode = ref(false)
const compareDividerPct = ref(50)
const isDraggingDivider = ref(false)

// Той самий ключ, що й в основному плеєрі — налаштування апскейлу спільні
const SHADER_STORAGE_KEY = 'anime4k_settings'

const loadShaderSettings = () => {
  if (!import.meta.client) return
  try {
    const saved = JSON.parse(localStorage.getItem(SHADER_STORAGE_KEY))
    if (!saved) return
    if (saved.mode !== undefined) shaderMode.value = saved.mode
    if (saved.quality !== undefined) shaderQuality.value = saved.quality
    if (saved.scale !== undefined) shaderScale.value = saved.scale
    if (saved.compareMode !== undefined) compareMode.value = saved.compareMode
  } catch (e) {}
}

const saveShaderSettings = () => {
  if (!import.meta.client) return
  try {
    localStorage.setItem(
      SHADER_STORAGE_KEY,
      JSON.stringify({
        mode: shaderMode.value,
        quality: shaderQuality.value,
        scale: shaderScale.value,
        compareMode: compareMode.value
      })
    )
  } catch (e) {}
}

// Гучність зберігаємо окремо від основного плеєра: там вона прив'язана
// до конкретного аніме, а тут це просто налаштування сторінки
const VOLUME_STORAGE_KEY = 'live_volume'

const loadVolumeSettings = () => {
  if (!import.meta.client) return
  try {
    const saved = JSON.parse(localStorage.getItem(VOLUME_STORAGE_KEY))
    if (!saved) return
    if (typeof saved.volume === 'number') volume.value = saved.volume
    if (typeof saved.muted === 'boolean') muted.value = saved.muted
  } catch (e) {}
}

const saveVolumeSettings = () => {
  if (!import.meta.client) return
  try {
    localStorage.setItem(
      VOLUME_STORAGE_KEY,
      JSON.stringify({ volume: volume.value, muted: muted.value })
    )
  } catch (e) {}
}

// --- Утиліти ---
const formatDuration = (seconds) => {
  if (!seconds || !isFinite(seconds) || seconds < 0) return '00:00'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  const pad = (n) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`
}

// Частоту кадрів дописуємо до підпису, коли рівень плавний: у драбині
// лишився один 1080p, і він саме шістдесятикадровий — але глядачеві важливо
// бачити, що це не звичайні 30. У конфігах цей рівень зветься просто
// «1080p» (див. streaming/README.md), а «60» живе тільки тут, у плеєрі —
// так підписують і Twitch, і YouTube
const SMOOTH_FPS = 50

const levelFps = (level) =>
  Math.round(level?.frameRate || Number(level?.attrs?.['FRAME-RATE']) || 0)

// Рівень без роздільності — це «тільки звук»: у мастер-плейлисті в нього
// свідомо немає RESOLUTION. Технічно відео там усе одно є (RTMP без
// відеодоріжки OBS не шле), але це картинка 256×144 на 100 kbps —
// показувати її як «144p» означало б обіцяти глядачеві не те
const levelLabel = (level) => {
  if (!level) return ''
  if (level.height) {
    const fps = levelFps(level)
    return fps >= SMOOTH_FPS ? `${level.height}p${fps}` : `${level.height}p`
  }
  return 'Тільки звук'
}

// --- Керування показом контролів ---
const { isOutside } = useMouseInElement(playerRef)
const { idle } = useIdle(4 * 1000)
const controlsVisible = computed(
  () => !playing.value || (!isOutside.value && !idle.value)
)

watchEffect(() => emit('update:controlsVisible', controlsVisible.value))

// --- Fullscreen ---
const isFullscreen = ref(false)
const {
  isSupported: orientSupported,
  lockOrientation,
  unlockOrientation
} = useScreenOrientation()

const handleFullscreenChange = () => {
  const doc = document
  isFullscreen.value = !!(
    doc.fullscreenElement ||
    doc.webkitFullscreenElement ||
    doc.mozFullScreenElement ||
    doc.msFullscreenElement
  )
}

// iPhone не вміє фулскрін для довільного елемента — там лишається тільки
// нативний плеєр самого <video> (див. коментар в основному плеєрі)
const enterNativeVideoFullscreen = () => {
  const video = videoRef.value
  if (!video || typeof video.webkitEnterFullscreen !== 'function') return false
  if (video.webkitSupportsFullscreen === false) return false
  video.webkitEnterFullscreen()
  return true
}

// Нативний плеєр iOS живе окремо від Fullscreen API: подій документа він не
// шле, тому стан тримаємо самі — інакше кнопка після виходу лишалась би в
// положенні «згорнути», і другий тап нічого не робив
const nativeFullscreen = ref(false)

// CSS-фулскрін: сторінку розтягує сама сторінка (див. live.vue). Потрібен
// там, де Fullscreen API недоступний — насамперед на iPhone
const pseudoFullscreen = ref(false)
watchEffect(() => emit('update:pseudoFullscreen', pseudoFullscreen.value))

const onNativeFullscreen = (active) => {
  nativeFullscreen.value = active
  isFullscreen.value = active

  if (!orientSupported.value) return

  if (active) {
    lockOrientation('landscape-primary').catch(() => {})
  } else {
    unlockOrientation()
  }
}

// Чи вміє елемент розгортатись на весь екран. На iPhone — ні (див. коментар
// вище), і перевірити це треба ДО спроби: інакше ловимо відмову проміса й
// лише потім відкочуємось на нативний плеєр, втрачаючи user gesture
const canFullscreenElement = (el) =>
  !!(el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen)

const toggleFullScreen = async () => {
  // Розгортаємо всю сторінку (плеєр + чат), а не самий плеєр
  const el = props.fullscreenTarget || playerRef.value
  if (!el) return

  if (!isFullscreen.value) {
    // iPhone: справжнього фулскріна для елемента там немає взагалі. Коли чат
    // закритий, втрачати нічого — віддаємо відео нативному плеєру iOS
    // (справжній фулскрін без адресного рядка). Коли відкритий — нативний
    // плеєр з'їв би і наші контроли, і чат, тож просто розтягуємо сторінку
    // стилями: адресний рядок Safari лишиться, зате все інше поводиться так
    // само, як на ПК
    if (!canFullscreenElement(el)) {
      if (!props.chatOpen && enterNativeVideoFullscreen()) {
        onNativeFullscreen(true)
        return
      }
      pseudoFullscreen.value = true
      isFullscreen.value = true
      return
    }

    try {
      if (el.requestFullscreen) await el.requestFullscreen()
      else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen()
      else if (el.msRequestFullscreen) await el.msRequestFullscreen()

      if (orientSupported.value) {
        try {
          await lockOrientation('landscape-primary')
        } catch (e) {}
      }
    } catch (err) {
      // API є, але дозволу не дав (буває у вбудованих вебв'ю) — відкочуємось
      // на той самий CSS-фулскрін, що й на iPhone, аби не втратити чат
      pseudoFullscreen.value = true
      isFullscreen.value = true
    }
  } else {
    if (pseudoFullscreen.value) {
      pseudoFullscreen.value = false
      isFullscreen.value = false
      return
    }

    if (nativeFullscreen.value) {
      videoRef.value?.webkitExitFullscreen?.()
      onNativeFullscreen(false)
      return
    }

    try {
      const doc = document

      if (doc.exitFullscreen) await doc.exitFullscreen()
      else if (doc.webkitExitFullscreen) await doc.webkitExitFullscreen()

      if (orientSupported.value) unlockOrientation()
    } catch (err) {
      isFullscreen.value = false
    }
  }
}

// Подвійний клік по картинці розгортає ефір на весь екран — так само, як у
// ютубі й твічі. Тільки з мишею: на тачскріні подвійний тап означає зум
// сторінки, і перехоплювати його, щоб замість цього стрибнути у фулскрін,
// точно не варто (значення ставиться в onMounted разом з рештою перевірок
// пристрою)
const coarsePointer = ref(false)

const onVideoDblclick = () => {
  if (coarsePointer.value) return
  toggleFullScreen()
}

// --- Картинка в картинці --- (обидва API див. utils/pip.js)
const pipSupported = ref(false)
const pipActive = ref(false)

const detectPipSupport = () => {
  pipSupported.value = canUsePip(videoRef.value)
}

const togglePip = () => switchPip(videoRef.value)

// WebKit шле одну подію і на PiP, і на фулскрін нативного плеєра —
// розрізняє їх лише сам режим
const onPresentationModeChange = () => {
  pipActive.value =
    videoRef.value?.webkitPresentationMode === 'picture-in-picture'
}

// --- Вибір транспорту HLS (та сама логіка, що й в основному плеєрі) ---
const isWebKitBrowser = () => {
  if (!import.meta.client) return false
  const ua = navigator.userAgent
  if (/iPhone|iPad|iPod/.test(ua)) return true
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return true // iPadOS
  return (
    /Safari\//.test(ua) && !/Chrome\/|Chromium\/|Edg\/|OPR\/|Firefox\//.test(ua)
  )
}

// Для прямого ефіру буфер потрібен зовсім інший, ніж для запису: великий
// forward-буфер лише збільшує затримку, а назад тримаємо годину, щоб
// працювала перемотка по DVR-вікну.
//
// Окремого режиму «низька затримка» тут свідомо немає. Він був, і виграшу
// не давав: lowLatencyMode в hls.js розрахований на LL-HLS (часткові
// сегменти + блокуючі запити плейлиста), а SRS віддає звичайний HLS
// цілими сегментами — вмикати його означало лише зрізати запас буфера до
// одного сегмента й підганяти швидкість відтворення. На виході — вічні
// підвантаження і смикання замість обіцяних секунд.
//
// Скільки дозволяємо відставати від краю. Перевищивши цю межу, hls.js САМ
// перемотує до живого — саме це й ламало перемотку: при вікні запису в дві
// години ліміт 600 × TARGETDURATION давав лише годину, і відмотка далі
// поверталась назад через пару секунд.
//
// Ставимо завідомо більше за будь-яке розумне вікно DVR: межу має задавати
// глибина запису на сервері, а не клієнт.
const MAX_LATENCY_COUNT = 100000

// Скільки лишаємо від самого краю плейлиста, коли стрибаємо в ефір.
// Нуль ставити не можна: останній сегмент може ще дописуватись, і плеєр
// почав би з підвантаження. Півтори секунди — це вже «наживо» на слух,
// але достатньо, щоб потрапити в готові дані
const EDGE_GAP = 1.5

// Скільки сегментів позаду краю тримається hls.js під час звичайного
// відтворення. Це і є основна затримка ефіру: на 4-секундних сегментах
// кожна одиниця — це 4 секунди. Тримаємо 2 замість типових 3: одного
// сегмента запасу вистачає, щоб пережити затримку наступного, а секунди
// економляться помітні
const SYNC_COUNT = 2

const hlsConfig = () => ({
  lowLatencyMode: false,
  backBufferLength: isMobile.value ? 600 : 3600,
  maxBufferLength: 30,
  maxMaxBufferLength: 120,
  maxBufferSize: (isMobile.value ? 60 : 150) * 1000 * 1000,
  liveSyncDurationCount: SYNC_COUNT,
  // Скільки дозволяємо відставати від краю ефіру. hls.js, перевищивши цю
  // межу, САМ стрибає назад до краю — саме через це перемотка по DVR
  // раніше не трималась: відмотав, а через пару секунд тебе повертало.
  // Ставимо величезне значення: мотай куди хочеш у межах вікна запису
  liveMaxLatencyDurationCount: MAX_LATENCY_COUNT,
  // Нижня межа для АВТОвибору якості. Тримає авторежим подалі від рівня
  // «тільки звук»: на секундному просіданні мережі hls.js інакше радо
  // перекине глядача на 232 kbps, а це картинка 256×144 — мило на весь
  // екран. Вибрати цей рівень руками ніщо не заважає, обмеження стосується
  // лише автоматики
  minAutoBitrate: 400000,
  enableWorker: true,
  progressive: false,
  fragLoadingMaxRetry: 10,
  fragLoadingRetryDelay: 1000,
  manifestLoadingMaxRetry: 2,
  levelLoadingMaxRetry: 4
})

// --- Ініціалізація / переініціалізація ---
const RETRY_DELAY = 15
let retryTimer = null

const clearRetry = () => {
  if (retryTimer) {
    clearInterval(retryTimer)
    retryTimer = null
  }
  retryIn.value = 0
}

const scheduleRetry = () => {
  clearRetry()
  retryIn.value = RETRY_DELAY
  retryTimer = setInterval(() => {
    retryIn.value--
    if (retryIn.value <= 0) {
      clearRetry()
      initPlayer()
    }
  }, 1000)
}

const destroyHls = () => {
  if (hlsInstance.value) {
    hlsInstance.value.destroy()
    hlsInstance.value = null
  }
  levels.value = []
  currentLevel.value = -1
  activeLevelLabel.value = ''
  activeLevelAudioOnly.value = false
  usingNativeHls.value = false
  // Новий інстанс — нова позиція в ефірі: стан підтягування скидаємо,
  // інакше свіжий плеєр успадкував би «глядач відмотав назад» від старого
  stopCatchUp()
  dvrMode.value = false
  playbackRate.value = 1
  liveTolerance.value = LIVE_EDGE
  lastHardCatchUp = 0
}

const initPlayer = () => {
  if (!import.meta.client || !videoRef.value || !props.src) return

  destroyHls()
  clearRetry()
  failed.value = false

  const nativeHlsSupported = videoRef.value.canPlayType(
    'application/vnd.apple.mpegurl'
  )

  if ((!nativeHlsSupported || !isWebKitBrowser()) && Hls?.isSupported()) {
    const hls = new Hls(hlsConfig())
    hlsInstance.value = hls

    hls.loadSource(props.src)
    hls.attachMedia(videoRef.value)

    hls.on(Hls.Events.MANIFEST_PARSED, (_e, data) => {
      streamOffline.value = false
      levels.value = (data.levels || []).map((lvl, index) => ({
        index,
        height: lvl.height,
        bitrate: lvl.bitrate,
        label: levelLabel(lvl)
      }))
      currentLevel.value = hls.currentLevel
      videoRef.value?.play().catch(() => {})
    })

    hls.on(Hls.Events.LEVEL_SWITCHED, (_e, data) => {
      const lvl = hls.levels?.[data.level]
      activeLevelLabel.value = lvl ? levelLabel(lvl) : ''
      activeLevelAudioOnly.value = !!lvl && !lvl.height
      // Ручний вибір не чіпаємо — сюди прилітає і автоперемикання
      if (hls.autoLevelEnabled) currentLevel.value = -1
    })

    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (!data.fatal) return

      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          // Mux віддає 404, поки live stream не запущено — це не поломка,
          // а «стрім ще не почався», тож просто чекаємо й пробуємо знову
          if (
            data.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR ||
            data.details === Hls.ErrorDetails.MANIFEST_PARSING_ERROR ||
            data.details === Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT
          ) {
            streamOffline.value = true
            destroyHls()
            scheduleRetry()
          } else {
            hls.startLoad()
          }
          break
        case Hls.ErrorTypes.MEDIA_ERROR:
          hls.recoverMediaError()
          break
        default:
          destroyHls()
          failed.value = true
          break
      }
    })
  } else {
    // Нативний HLS — Safari, iOS та Smart TV
    usingNativeHls.value = true
    nativeMasterSrc.value = props.src
    attachNativeSource(props.src)
    loadNativeLevels(props.src)
  }
}

// --- Рівні якості на нативному HLS ---
//
// Safari (а отже й геть усі браузери на iPhone та iPad) грає HLS сам і
// назовні про рівні не розповідає нічого: ні списку, ні способу вибрати.
// Через це в меню на айфонах не було чого показати — лишалась сама
// «затримка», і глядач не міг ані злізти на 480p у метро, ані примусово
// сісти на 1080p, коли автоматика Apple вирішила інакше.
//
// Тому мастер-плейлист розбираємо самі, а вибраний рівень вмикаємо
// єдиним доступним способом — підставляємо у <video> дочірній плейлист
// замість мастера. «Авто» — це повернення мастера, тобто рідна автоматика
// Safari.

// Значення атрибутів EXT-X-STREAM-INF. Ліниво розбити рядок по комі не
// можна: CODECS="avc1.4d401f,mp4a.40.2" — це ОДИН атрибут із комою всередині
const ATTR_RE = /([A-Z0-9-]+)=("[^"]*"|[^,]*)/g

const parseAttrs = (line) => {
  const attrs = {}
  let m
  ATTR_RE.lastIndex = 0
  while ((m = ATTR_RE.exec(line)) !== null) {
    attrs[m[1]] = m[2].replace(/^"|"$/g, '')
  }
  return attrs
}

const parseMasterPlaylist = (text, baseUrl) => {
  const lines = text.split('\n')
  const variants = []

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line.startsWith('#EXT-X-STREAM-INF:')) continue

    const attrs = parseAttrs(line.slice('#EXT-X-STREAM-INF:'.length))

    // Посилання на рівень — перший наступний рядок, який не тег і не порожній
    let uri = ''
    for (let j = i + 1; j < lines.length; j++) {
      const next = lines[j].trim()
      if (!next || next.startsWith('#')) continue
      uri = next
      i = j
      break
    }
    if (!uri) continue

    let url
    try {
      url = new URL(uri, baseUrl).toString()
    } catch (e) {
      continue
    }

    const height = Number(attrs.RESOLUTION?.split('x')[1]) || 0
    variants.push({
      url,
      height,
      // Рівень без RESOLUTION — це «тільки звук» (див. streaming/README.md)
      frameRate: Number(attrs['FRAME-RATE']) || 0,
      bitrate: Number(attrs.BANDWIDTH) || 0
    })
  }

  // hls.js віддає рівні від найгіршого до найкращого, і верстка меню
  // розрахована саме на цей порядок — тримаємо той самий
  return variants.sort((a, b) => a.bitrate - b.bitrate)
}

const loadNativeLevels = async (masterUrl) => {
  try {
    const res = await fetch(masterUrl)
    if (!res.ok) return

    const variants = parseMasterPlaylist(await res.text(), res.url || masterUrl)
    // Один рівень (або взагалі медіа-плейлист без драбини) — вибирати нічого
    if (variants.length < 2) return
    // Поки качався плейлист, ефір міг переїхати на hls.js, зникнути або
    // отримати нове посилання — тоді розібрані рівні вже не про нього
    if (!usingNativeHls.value || nativeMasterSrc.value !== masterUrl) return

    levels.value = variants.map((v, index) => ({
      index,
      height: v.height,
      bitrate: v.bitrate,
      label: levelLabel(v),
      url: v.url
    }))
    syncNativeActiveLevel()
  } catch (e) {
    // Плейлист не дали (CORS, мережа) — лишаємось із самим «Авто», як було
  }
}

// Який рівень грає в авторежимі, нативний плеєр теж не каже — але каже
// розмір картинки, а він у драбині унікальний для кожного рівня
const syncNativeActiveLevel = () => {
  const video = videoRef.value
  if (!video || !usingNativeHls.value || !levels.value.length) return

  const height = video.videoHeight
  const match = levels.value.find((l) => l.height && l.height === height)
  if (match) {
    activeLevelLabel.value = match.label
    activeLevelAudioOnly.value = false
    return
  }

  // 256×144 — це наш звуковий рівень: у нього немає RESOLUTION, тож у
  // списку його висота нульова і зіставити по ній не вийде
  const audioOnly = levels.value.find((l) => !l.height)
  if (audioOnly && height > 0 && height <= 144) {
    activeLevelLabel.value = audioOnly.label
    activeLevelAudioOnly.value = true
    return
  }

  activeLevelLabel.value = height ? `${height}p` : ''
  activeLevelAudioOnly.value = false
}

// Джерело для нативного плеєра. Тим самим шляхом і перемикаємо якість:
// restore тримає, скільки ми відставали від краю ефіру, щоб після
// перезавантаження плейлиста глядач лишився на тому ж місці, а не поїхав
// у прямий ефір із середини запису
const attachNativeSource = (url, restore = null) => {
  const video = videoRef.value
  if (!video) return

  // Перемикання якості не має скидати паузу: продовжуємо грати лише те,
  // що грало. Перший запуск — навпаки, завжди пробуємо стартувати
  const shouldPlay = restore ? !video.paused : true
  // Рівень міг просто не публікуватись (OBS не шле цей вихід) — тоді це не
  // падіння ефіру, а привід тихо вернутись на мастер-плейлист
  const isLevel = url !== nativeMasterSrc.value

  const detach = () => {
    video.removeEventListener('loadedmetadata', onMeta)
    video.removeEventListener('error', onError)
  }

  const onMeta = () => {
    detach()
    streamOffline.value = false
    syncDvr()

    if (restore) {
      const range = video.seekable
      const edge = range?.length ? range.end(range.length - 1) : 0
      if (edge > 0) {
        // Нарізка сегментів у всіх рівнів однакова (спільний keyint,
        // див. streaming/README.md), тож відступ від краю переноситься як є
        const target =
          restore.behind > liveTolerance.value
            ? edge - restore.behind
            : edge - EDGE_GAP
        video.currentTime = Math.max(dvrStart.value, target)
        position.value = video.currentTime
      }
      video.playbackRate = playbackRate.value
    }

    syncNativeActiveLevel()
    if (shouldPlay) video.play().catch(() => {})
  }

  const onError = () => {
    detach()

    if (isLevel) {
      currentLevel.value = -1
      attachNativeSource(nativeMasterSrc.value, restore)
      return
    }

    streamOffline.value = true
    scheduleRetry()
  }

  video.addEventListener('loadedmetadata', onMeta)
  video.addEventListener('error', onError)
  video.src = url
  video.load()
}

watch(
  () => props.src,
  () => initPlayer()
)

const togglePlay = () => {
  const video = videoRef.value
  if (!video) return
  if (video.paused) video.play().catch(() => {})
  else video.pause()
}

// --- Гучність ---
const VOLUME_STEP = 0.05

const setVolume = (value) => {
  const next = Math.max(0, Math.min(1, value))
  volume.value = Math.round(next * 100) / 100
  if (next > 0 && muted.value) muted.value = false
}

const onVolumeWheel = (e) => {
  const base = muted.value ? 0 : Number(volume.value) || 0
  setVolume(base + (e.deltaY < 0 ? VOLUME_STEP : -VOLUME_STEP))
}

watch([volume, muted], () => {
  const video = videoRef.value
  if (video) {
    video.volume = Number(volume.value) || 0
    video.muted = muted.value
  }
  saveVolumeSettings()
})

// --- Якість ---
const setLevel = (index) => {
  currentLevel.value = index

  if (hlsInstance.value) {
    hlsInstance.value.currentLevel = index
    return
  }

  // Нативний плеєр рівня не має — міняємо йому плейлист під ногами
  if (!usingNativeHls.value) return

  const level = index === -1 ? null : levels.value.find((l) => l.index === index)
  const url = index === -1 ? nativeMasterSrc.value : level?.url
  if (!url) return

  // Заставку «тільки звук» вмикаємо одразу: у нативному шляху про рівень
  // ми знаємо з самого списку, чекати на розмір картинки нема сенсу
  activeLevelAudioOnly.value = !!level && !level.height
  if (level) activeLevelLabel.value = level.label

  attachNativeSource(url, {
    behind: Math.max(0, dvrEnd.value - position.value)
  })
}

// У списку якості лише реальні рівні потоку. Пунктів «2160p» і «1440p»
// тут свідомо немає: окремих потоків такої роздільності не існує, це були
// пресети Anime4K поверх найвищого рівня — тобто підпис обіцяв 4K, а
// віддавав домальоване з 1080p. Сам апскейл нікуди не подівся: він живе в
// окремому меню з іконкою іскор, де чесно зветься апскейлом
//
// Значення для селекта якості: 'auto' | 'level:<index>'
const qualityValue = computed({
  get: () => (currentLevel.value === -1 ? 'auto' : `level:${currentLevel.value}`),
  set: (value) => {
    if (value === 'auto') return setLevel(-1)
    setLevel(Number(value.slice(6)))
  }
})

// Червона крапка «LIVE» = наш потік реально йде. Не беремо для неї
// props.isLive: то статус на Twitch, і він не збігається з ефіром у Mux
// (Twitch може бути офлайн, поки Mux уже роздає потік)
const streamLive = computed(() => !streamOffline.value && !failed.value)

// --- DVR: перемотка ефіру назад ---
//
// Mux тримає вікно запису, і його межі віддає сам <video> через seekable.
// Рахувати від startedAt (час старту стріму на Twitch) не можна: він про
// інший потік і з нашим DVR-вікном не збігається.
const LIVE_EDGE = 12 // с — ближче за це вважаємо, що дивимось наживо
const dvrStart = ref(0)
const dvrEnd = ref(0)
const position = ref(0)

// Скільки плеєр законно відстає від краю навіть коли дивишся «наживо».
// hls.js СВІДОМО тримається на liveSyncDurationCount сегментів позаду краю
// (це його буфер на випадок затримки сегмента), тож на наших 4-секундних
// сегментах SRS «жива» позиція — це вже -8…-12 с від кінця плейлиста.
// Порівнювати таке з голими 12 секундами не можна: у Chrome (hls.js) значок
// «відстаємо» загорявся у людини, яка щойно відкрила ефір і нічого не мотала,
// а в Safari (нативний HLS сам тримає синхронізацію) — ні.
//
// Тому межу беремо з самого hls.js: targetLatency — це та затримка, до якої
// він і прагне. Все, що більше за неї з запасом, — вже справжній дрейф.
const NATURAL_SLACK = 8 // с запасу понад природну затримку
const liveTolerance = ref(LIVE_EDGE)

// Глядач сам пішов з ефіру: відмотав повзунком або поставив на паузу.
// Поки прапорець стоїть, автопідтягування до краю мовчить — інакше воно
// боролося б із перемоткою, заради якої DVR і робився
const dvrMode = ref(false)

const dvrLength = computed(() => Math.max(0, dvrEnd.value - dvrStart.value))
// Поки вікно куце (перші секунди ефіру), скраб ховаємо — мотати нікуди
const dvrAvailable = computed(() => dvrLength.value > 30)
const atLiveEdge = computed(
  () => dvrEnd.value - position.value <= liveTolerance.value
)
const behindLive = computed(() =>
  Math.max(0, Math.round(dvrEnd.value - position.value))
)

const syncDvr = () => {
  const video = videoRef.value
  if (!video) return

  const range = video.seekable
  if (range?.length) {
    dvrStart.value = range.start(0)
    dvrEnd.value = range.end(range.length - 1)
  }

  position.value = video.currentTime

  // targetLatency з'являється лише після завантаження плейлиста і росте,
  // якщо ефір підвисав, — тож перечитуємо його щоразу
  const target = hlsInstance.value?.targetLatency
  liveTolerance.value =
    target > 0 ? Math.max(LIVE_EDGE, target + NATURAL_SLACK) : LIVE_EDGE
}

// timeupdate — єдине місце, звідки перевіряємо дрейф: він приходить лише
// поки відео реально грає (≈4 рази на секунду), а саме дрейф під час
// відтворення нас і цікавить. Викликати перевірку з syncDvr не можна:
// goLive сам смикає syncDvr, і вийшла б рекурсія
const onTimeUpdate = () => {
  syncDvr()
  enforceLiveRateLimit()
  catchUpToLive()
}

// Пауза — теж свідомий вихід з ефіру: людина повернеться туди, де зупинилась,
// і тягнути її вперед ми не маємо права
const onPause = () => {
  playing.value = false
  stopCatchUp()
  dvrMode.value = true
}

// Опинились біля краю (самі доїхали, натиснули LIVE чи пауза була короткою) —
// знову дозволяємо автопідтягування
watch(atLiveEdge, (edge) => {
  if (edge) dvrMode.value = false
})

const seekTo = (time) => {
  const video = videoRef.value
  if (!video) return

  // Впритул до кінця не стаємо: там ще немає завантаженого фрагмента
  const clamped = Math.max(dvrStart.value, Math.min(dvrEnd.value - 1, time))
  video.currentTime = clamped
  position.value = clamped
  // Свідома відмотка назад — далі не тягнемо людину до ефіру самі
  dvrMode.value = dvrEnd.value - clamped > liveTolerance.value
}

// Поки тягнуть повзунок, показуємо намір, а не реальну позицію відео
const pendingSeek = ref(null)
let seekCommitTimer = null

// Позиція для скраба — від нуля, інакше на екрані були б голі timestamp-и
const scrubValue = computed({
  get: () =>
    pendingSeek.value ??
    Math.max(0, Math.min(dvrLength.value, position.value - dvrStart.value)),
  set: (v) => {
    // Скрабер шле нове значення на КОЖЕН рух миші. Якщо на кожне з них
    // смикати currentTime, hls.js отримує десятки перемоток за секунду,
    // захлинається перезапитами сегментів і врешті кидає назад до краю —
    // саме так це й виглядало «перемотка не працює». Тому позицію на
    // смужці рухаємо одразу, а саме відео перемотуємо один раз, коли рух
    // припинився
    pendingSeek.value = v

    clearTimeout(seekCommitTimer)
    seekCommitTimer = setTimeout(() => {
      seekTo(dvrStart.value + (pendingSeek.value ?? 0))
      pendingSeek.value = null
    }, 180)
  }
})

// Крок перемотки стрілками. П'ять секунд — як у YouTube: серією натискань
// перескакуєш паузу в ефірі, і водночас важко промазати повз момент,
// заради якого мотають
const SEEK_STEP = 5

// Мотаємо через scrubValue, а не seekTo: там уже є затримка перед справжньою
// перемоткою, тож серія натискань складається в ОДИН стрибок замість десятка
// перезапитів сегментів у hls.js. Заразом рухається й сама смужка
const seekBy = (delta) => {
  scrubValue.value = Math.max(
    0,
    Math.min(dvrLength.value, scrubValue.value + delta)
  )
}

// --- Режим перегляду запису ---
//
// Смужку перемотки на прямому ефірі за замовчуванням не показуємо: людина
// прийшла дивитись наживо, а зайвий скраб лише плутає й провокує випадкові
// відмотки. Тому ховаємо її за кнопкою «Запис»: натиснув — і з'явились
// і перемотка по DVR-вікну, і швидкість відтворення.
const recordMode = ref(false)

const toggleRecordMode = () => {
  recordMode.value = !recordMode.value
  if (recordMode.value) return

  // Вимкнули запис — це для глядача те саме, що «повернутись у ефір»
  if (!atLiveEdge.value) return goLive()
  // Біля краю goLive не потрібен, але прискорення лишилось би ввімкненим —
  // і без смужки запису його вже ніде вимкнути
  playbackRate.value = 1
}

// --- Таймінг ефіру ---
//
// Показуємо, на якій хвилині самого стріму ми зараз, а не скільки відстаємо
// від краю: «-1:23» нічого не каже про те, що відбувається на екрані.
// Відлік ведемо від started_at (Twitch), а не від початку DVR-вікна — вікно
// коротше за стрім і його нуль не збігається з початком ефіру.
const streamTimeAt = (time) => {
  if (uptime.value === null) return Math.max(0, Math.round(time - dvrStart.value))
  const behind = Math.max(0, dvrEnd.value - time)
  return Math.max(0, Math.round(uptime.value - behind))
}

// Поки тягнуть повзунок, час рахуємо від наміру (pendingSeek всередині
// scrubValue), інакше цифри стрибали б назад до реальної позиції відео
const scrubStreamTime = computed(() =>
  streamTimeAt(dvrStart.value + scrubValue.value)
)

const totalStreamTime = computed(() =>
  uptime.value === null ? Math.round(dvrLength.value) : uptime.value
)

// --- Дебаг-панель ---
//
// Показує те, що для ефіру справді болить: відставання від краю, глибину
// DVR-вікна, буфер і поточний рівень. Оновлюється лише поки ввімкнена —
// інтервал живе рівно стільки, скільки видно панель
const DEBUG_KEY = 'live_debug'
const showDebug = ref(false)
const debugStats = ref(null)
let debugTimer = null

const collectDebugStats = () => {
  const video = videoRef.value
  if (!video) return null

  const buffered = video.buffered
  let ahead = 0
  for (let i = 0; i < buffered.length; i++) {
    if (video.currentTime >= buffered.start(i) && video.currentTime <= buffered.end(i)) {
      ahead = buffered.end(i) - video.currentTime
      break
    }
  }

  const quality = video.getVideoPlaybackQuality?.()
  // В авто-режимі currentLevel = -1, тож реально відтворюваний рівень
  // питаємо в самого hls.js
  const activeIndex = hlsInstance.value
    ? hlsInstance.value.currentLevel
    : currentLevel.value
  // На нативному шляху в авторежимі індексу немає взагалі — там єдине, що
  // ми знаємо про рівень, це підпис, зіставлений за розміром картинки
  const level =
    levels.value.find((l) => l.index === activeIndex) ||
    (usingNativeHls.value && activeLevelLabel.value
      ? { label: activeLevelLabel.value, bitrate: 0 }
      : null)

  return {
    res: `${video.videoWidth}x${video.videoHeight}`,
    level: level
      ? level.bitrate
        ? `${level.label} @ ${Math.round(level.bitrate / 1000)} kbps`
        : level.label
      : '—',
    auto: currentLevel.value === -1 ? 'так' : 'ні',
    behind: `${behindLive.value} с`,
    // Норма — це природна затримка hls.js; усе понад неї і є дрейф, який
    // ми самі підтягуємо (швидкістю або стрибком)
    tolerance: `${Math.round(liveTolerance.value)} с${
      catchingUp.value ? ` · доганяємо ${CATCH_UP_RATE}x` : ''
    }${dvrMode.value ? ' · запис' : ''}`,
    dvr: `${Math.round(dvrLength.value)} с`,
    position: `${Math.round(position.value)} / ${Math.round(dvrEnd.value)}`,
    buffer: `${ahead.toFixed(1)} с`,
    dropped: quality ? quality.droppedVideoFrames : '—',
    shader: shaderActive.value ? `${shaderMode.value} / ${shaderScale.value}` : 'вимкнено',
    transport: hlsInstance.value ? 'hls.js' : 'нативний HLS'
  }
}

watch(showDebug, (value) => {
  try {
    localStorage.setItem(DEBUG_KEY, value ? '1' : '0')
  } catch (e) {}

  if (debugTimer) {
    clearInterval(debugTimer)
    debugTimer = null
  }

  if (!value) {
    debugStats.value = null
    return
  }

  debugStats.value = collectDebugStats()
  debugTimer = setInterval(() => (debugStats.value = collectDebugStats()), 500)
})

// Стрибок у найсвіжішу точку ефіру. Працює і коли ми нібито вже «наживо»:
// за довгий перегляд плеєр непомітно відстає на кілька секунд, і повторний
// клік по LIVE має підтягувати до самого краю

// --- Швидкість відтворення ---
//
// Має сенс лише коли дивимось із запису: наживо прискорення впирається в
// край ефіру за кілька секунд і нічого не дає. Зате коли відмотав назад —
// це спосіб наздогнати ефір, не пропускаючи побачене
const PLAYBACK_RATES = [1, 1.25, 1.5, 1.75, 2]
const playbackRate = ref(1)

watch(playbackRate, (rate) => {
  if (videoRef.value) videoRef.value.playbackRate = rate
})

// Окремої кнопки швидкості поруч зі смужкою немає свідомо: міняють її рідко,
// а місце в рядку вона з'їдала постійно. Живе в меню налаштувань потоку

// Скільки секунд буфера попереду вважаємо запасом, достатнім, щоб грати
// швидше за реальний час. На 2x кожна секунда стінного часу з'їдає дві
// секунди буфера, а перевіряємо ми це чотири рази на секунду — двох секунд
// із головою, і водночас це менше за один сегмент, тож щойно після
// перемотки прискорення не зникає само собою
const RATE_MIN_BUFFER = 2

const bufferAhead = () => {
  const video = videoRef.value
  if (!video) return 0

  const ranges = video.buffered
  for (let i = 0; i < ranges.length; i++) {
    if (video.currentTime >= ranges.start(i) && video.currentTime <= ranges.end(i)) {
      return ranges.end(i) - video.currentTime
    }
  }
  return 0
}

// Прискорення має сенс, лише поки попереду є що наздоганяти: біля краю
// ефіру відео попереду просто ще не існує, і 2x з'їдає буфер удвічі
// швидше, ніж той приходить.
//
// Перевіряти це переходом «доїхали до краю» (watch по atLiveEdge) було
// недостатньо: якщо швидкість увімкнули, ВЖЕ стоячи біля краю, переходу не
// стається зовсім — прискорення лишалось назавжди, плеєр вичерпував буфер
// і зависав намертво. Тому дивимось стан, а не зміну, і робимо це на кожен
// timeupdate плюс на кожне підвисання
// Перемотка (стрілками, смужкою, кнопками) — не підвисання. Нове місце ще
// не в буфері, тож браузер шле waiting, а буфера попереду нуль — і обидві
// перевірки нижче скидали швидкість на 1x щоразу, коли глядач мотав запис.
// Тому під час перемотки й кілька секунд після неї перевірки буфера мовчать:
// стільки потрібно, щоб догрузився сегмент на новому місці
const SEEK_GRACE = 4000
let seekGraceUntil = 0

const onSeeking = () => {
  seekGraceUntil = Date.now() + SEEK_GRACE
}

const inSeekGrace = () => !!videoRef.value?.seeking || Date.now() < seekGraceUntil

const enforceLiveRateLimit = () => {
  if (playbackRate.value === 1) return
  // Край ефіру — причина скинути швидкість завжди, навіть одразу після
  // перемотки: попереду там просто нічого немає
  if (atLiveEdge.value) {
    playbackRate.value = 1
    return
  }
  if (inSeekGrace()) return
  if (bufferAhead() < RATE_MIN_BUFFER) playbackRate.value = 1
}

// Підвисли з прискоренням — воно ж і винне. Скидаємо навіть якщо до краю
// ще далеко: на поганій мережі 2x лише поглиблює яму, з якої глядач інакше
// вибереться хіба перезавантаженням сторінки. Крім очікування після
// перемотки — це догрузка нового місця, а не яма
const onWaiting = () => {
  waiting.value = true
  if (inSeekGrace()) return
  if (playbackRate.value !== 1) playbackRate.value = 1
}

// --- Автопідтягування до ефіру ---
//
// hls.js, на відміну від нативного HLS у Safari, у нашій конфігурації НІКОЛИ
// не наздоганяє ефір сам:
//   • жорсткий стрибок до краю ми вимкнули (liveMaxLatencyDurationCount),
//     бо він ламав перемотку по DVR;
//   • підганяння швидкістю всередині hls.js працює лише в lowLatencyMode,
//     а він у нас вимкнений назавжди (див. коментар до hlsConfig).
// Тому кожне підвисання, приспаний ноут чи перезапит сегмента додавали
// затримки НАЗАВЖДИ: у глядача Chrome накопичувалось «-1:30», хоча він
// нічого не мотав. Safari цього не показував, бо нативний плеєр
// синхронізується сам. Отже, наздоганяємо власноруч.
const DRIFT_SOFT = 5 // с дрейфу понад норму — підганяємо швидкістю
const DRIFT_HARD = 30 // с дрейфу — швидкістю доганяти надто довго, стрибаємо
const CATCH_UP_RATE = 1.1 // непомітно на слух, 10 с дрейфу з'їдає за ~100 с
const CATCH_UP_COOLDOWN = 30000 // мс між стрибками, щоб не смикати на поганій мережі

const catchingUp = ref(false)
let lastHardCatchUp = 0

const stopCatchUp = () => {
  if (!catchingUp.value) return
  catchingUp.value = false
  if (videoRef.value) videoRef.value.playbackRate = playbackRate.value
}

const catchUpToLive = () => {
  const video = videoRef.value
  if (!video) return

  // Не чіпаємо: свідому перемотку, паузу, ручне прискорення (людина вже
  // доганяє сама) і момент, коли відео й так стоїть без даних
  if (dvrMode.value || video.paused || playbackRate.value !== 1) {
    stopCatchUp()
    return
  }

  const drift = dvrEnd.value - position.value - liveTolerance.value

  if (drift <= 0) {
    stopCatchUp()
    return
  }

  if (drift >= DRIFT_HARD) {
    stopCatchUp()
    // Стрибаємо лише коли відтворення здорове: якщо буфера попереду нема,
    // ми не «відстали й забули», а прямо зараз задихаємось — стрибок до
    // краю в такому стані лише додасть ще одне підвантаження
    if (video.readyState < 3) return
    if (Date.now() - lastHardCatchUp < CATCH_UP_COOLDOWN) return
    lastHardCatchUp = Date.now()
    goLive()
    return
  }

  // Дрібний дрейф з'їдаємо швидкістю: стрибок тут помітніший за саму
  // затримку — це і кадр, що смикнувся, і перезавантаження буфера
  if (drift > DRIFT_SOFT) {
    if (!catchingUp.value) {
      catchingUp.value = true
      video.playbackRate = CATCH_UP_RATE
    }
  } else {
    stopCatchUp()
  }
}

const goLive = () => {
  const video = videoRef.value
  if (!video) return

  syncDvr()

  pendingSeek.value = null
  clearTimeout(seekCommitTimer)
  stopCatchUp()
  dvrMode.value = false
  // Наздоганяти більше нічого — прискорення тут лише вичерпає буфер
  playbackRate.value = 1

  // Ціль — найсвіжіша точка ефіру, а не liveSyncPosition.
  //
  // Раніше ми стрибали саме на liveSyncPosition — точку, яку hls.js тримає
  // за «живу». Але вона за визначенням відстає на liveSyncDurationCount
  // сегментів, тобто на наших сегментах це 8-12 секунд: людина тиснула
  // LIVE і лишалась у минулому. Тепер ідемо до самого краю плейлиста,
  // лишаючи тільки запас EDGE_GAP — щоб не стати впритул до сегмента,
  // який ще пишеться, і не почати з підвантаження.
  //
  // Нативний HLS (Safari) liveSyncPosition не має — там край seekable і
  // був єдиним орієнтиром
  const range = video.seekable
  const edge = range?.length ? range.end(range.length - 1) : dvrEnd.value

  video.currentTime = Math.max(dvrStart.value, edge - EDGE_GAP)
  position.value = video.currentTime

  video.play().catch(() => {})
}

const currentQualityLabel = computed(() => {
  if (currentLevel.value === -1) {
    return activeLevelLabel.value ? `Авто (${activeLevelLabel.value})` : 'Авто'
  }
  return levels.value.find((l) => l.index === currentLevel.value)?.label || 'Авто'
})

// --- Anime4K (порт з основного плеєра) ---
const checkShaderSupport = () => {
  if (!import.meta.client) return false
  try {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl')
    if (!gl) return false
    if (!gl.getExtension('OES_texture_float')) return false
    if (!gl.getExtension('OES_texture_float_linear')) return false
    return true
  } catch (e) {
    return false
  }
}

const ensureAnime4K = async () => {
  if (Anime4KJS) return true
  if (!import.meta.client) return false
  try {
    Anime4KJS = await import('anime4k.js')
    return true
  } catch (e) {
    shaderSupported.value = false
    return false
  }
}

const UPSCALE_STAGE_NAMES = [
  'Anime4K_Upscale_CNN_x2_S',
  'Anime4K_Upscale_CNN_x2_M',
  'Anime4K_Upscale_CNN_x2_L',
  'Anime4K_Upscale_CNN_x2_VL',
  'Anime4K_Upscale_CNN_x2_UL',
  'Anime4K_Upscale_Denoise_CNN_x2_S',
  'Anime4K_Upscale_Denoise_CNN_x2_M',
  'Anime4K_Upscale_Denoise_CNN_x2_L',
  'Anime4K_Upscale_Denoise_CNN_x2_VL',
  'Anime4K_Upscale_Denoise_CNN_x2_UL'
]

const capUpscaleStages = (config, keep) => {
  const upscaleClasses = new Set(
    UPSCALE_STAGE_NAMES.map((name) => Anime4KJS[name]).filter(Boolean)
  )
  const total = config.filter((ctor) => upscaleClasses.has(ctor)).length
  let seen = 0
  return config.filter((ctor) => {
    if (!upscaleClasses.has(ctor)) return true
    seen++
    return seen > total - keep
  })
}

const getShaderConfig = (mode, quality, scale) => {
  if (!Anime4KJS || mode === 'off') return null

  let config
  if (mode === 'restore') {
    const map = {
      s: Anime4KJS.Anime4K_Restore_CNN_S,
      m: Anime4KJS.Anime4K_Restore_CNN_M,
      l: Anime4KJS.Anime4K_Restore_CNN_L,
      vl: Anime4KJS.Anime4K_Restore_CNN_VL,
      ul: Anime4KJS.Anime4K_Restore_CNN_UL
    }
    const restoreCtor = map[quality] || Anime4KJS.Anime4K_Restore_CNN_M
    config = restoreCtor
      ? [Anime4KJS.Anime4K_Clamp_Highlights, restoreCtor]
      : null
  } else if (mode === 'simple') {
    const map = {
      s: Anime4KJS.ANIME4KJS_SIMPLE_S_2X,
      m: Anime4KJS.ANIME4KJS_SIMPLE_M_2X,
      l: Anime4KJS.ANIME4KJS_SIMPLE_L_2X,
      vl: Anime4KJS.ANIME4KJS_SIMPLE_VL_2X,
      ul: Anime4KJS.ANIME4KJS_SIMPLE_UL_2X
    }
    config = map[quality] || Anime4KJS.ANIME4KJS_SIMPLE_M_2X
  } else {
    const [tier, letter] = mode.split('-')
    const prefix =
      tier === 'lower' ? 'ANIME4K_LOWEREND_MODE_' : 'ANIME4K_HIGHEREND_MODE_'
    const suffix = letter.toUpperCase() + (quality === 'fast' ? '_FAST' : '')
    const preset = Anime4KJS[prefix + suffix]
    config = preset ? Anime4KJS.ignoreAutoDownscalePre(preset) : null
  }

  if (!config) return null
  return capUpscaleStages(config, scale === '4k' ? 1 : 0)
}

let compositorRaf = null

const stopCompositor = () => {
  if (compositorRaf != null) {
    cancelAnimationFrame(compositorRaf)
    compositorRaf = null
  }
}

const getDisplayTargetSize = () => {
  const video = videoRef.value
  const src = shaderCanvasRef.value
  if (!video || !src || !src.width || !src.height) return null

  if (shaderScale.value === '2k' && shaderMode.value !== 'restore') {
    const targetH = 1440
    const targetW = Math.round((video.videoWidth * targetH) / video.videoHeight)
    return { w: targetW, h: targetH }
  }
  return { w: src.width, h: src.height }
}

const runCompositor = () => {
  compositorRaf = requestAnimationFrame(runCompositor)

  const src = shaderCanvasRef.value
  const dst = shaderDisplayCanvasRef.value
  if (!src || !dst) return

  const size = getDisplayTargetSize()
  if (!size) return

  if (dst.width !== size.w || dst.height !== size.h) {
    dst.width = size.w
    dst.height = size.h
  }

  const ctx = dst.getContext('2d')
  ctx.drawImage(src, 0, 0, size.w, size.h)
}

const startCompositor = () => {
  if (compositorRaf != null) return
  runCompositor()
}

const destroyShader = () => {
  stopCompositor()
  if (videoUpscaler.value) {
    videoUpscaler.value.detachVideo()
    videoUpscaler.value = null
  }
}

const setupShader = () => {
  destroyShader()

  if (
    !Anime4KJS ||
    !shaderSupported.value ||
    shaderMode.value === 'off' ||
    !videoRef.value ||
    !shaderCanvasRef.value
  ) {
    return
  }

  const shaderConfig = getShaderConfig(
    shaderMode.value,
    shaderQuality.value,
    shaderScale.value
  )
  if (!shaderConfig) return

  const upscaler = new Anime4KJS.VideoUpscaler(shaderConfig)
  upscaler.attachVideo(videoRef.value, shaderCanvasRef.value)
  upscaler.start()
  videoUpscaler.value = upscaler
  startCompositor()
}

watch(shaderMode, async (mode, oldMode) => {
  if (mode === oldMode) return
  shaderQuality.value = defaultShaderQuality(mode)

  if (mode === 'off') {
    compareMode.value = false
    destroyShader()
    return
  }

  const ok = await ensureAnime4K()
  if (!ok) {
    shaderMode.value = 'off'
    return
  }
  setupShader()
})

watch(shaderQuality, () => {
  if (shaderMode.value !== 'off') setupShader()
})

watch(shaderScale, () => {
  if (shaderMode.value !== 'off') setupShader()
})

watch([shaderMode, shaderQuality, shaderScale, compareMode], saveShaderSettings)

// --- Режим порівняння: перетягування розділювача ---
const updateDividerFromEvent = (clientX) => {
  const el = compareContainerRef.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  const pct = ((clientX - rect.left) / rect.width) * 100
  compareDividerPct.value = Math.max(0, Math.min(100, pct))
}

const onDividerMousedown = (e) => {
  isDraggingDivider.value = true
  updateDividerFromEvent(e.clientX)
}
const onDividerTouchstart = () => (isDraggingDivider.value = true)
const onDocumentMousemove = (e) => {
  if (isDraggingDivider.value) updateDividerFromEvent(e.clientX)
}
const onDocumentMouseup = () => (isDraggingDivider.value = false)
const onDocumentTouchmove = (e) => {
  if (isDraggingDivider.value && e.touches?.[0]) {
    updateDividerFromEvent(e.touches[0].clientX)
  }
}
const onDocumentTouchend = () => (isDraggingDivider.value = false)

// --- Клавіатура ---
const keyHandler = (e) => {
  // Не перехоплюємо ввід, коли людина пише в чат або в будь-яке поле
  const tag = e.target?.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) {
    return
  }

  // Комбінації з модифікаторами — не наші: Cmd+C копіює виділене, Cmd+F
  // шукає по сторінці. Без цієї перевірки копіювання нікнейма з чату
  // згортало б сам чат, а пошук — розгортав плеєр на весь екран
  if (e.ctrlKey || e.metaKey || e.altKey) return

  switch (e.code) {
    case 'Space':
    case 'KeyK':
      e.preventDefault()
      togglePlay()
      break
    // Гучність — завжди: це єдина стрілка, яка означає одне й те саме
    // в будь-якому режимі
    case 'ArrowUp':
      e.preventDefault()
      setVolume((Number(volume.value) || 0) + VOLUME_STEP)
      break
    case 'ArrowDown':
      e.preventDefault()
      setVolume((Number(volume.value) || 0) - VOLUME_STEP)
      break
    // Перемотка — лише разом зі смужкою запису (та сама умова, що й у
    // верстці). На прямому ефірі стрілка нікуди не веде, а випадкове
    // натискання викинуло б глядача з ефіру в минуле без жодного натяку,
    // що взагалі сталось
    case 'ArrowLeft':
    case 'ArrowRight':
      if (!dvrAvailable.value || !recordMode.value) break
      e.preventDefault()
      seekBy(e.code === 'ArrowLeft' ? -SEEK_STEP : SEEK_STEP)
      break
    case 'KeyM':
      muted.value = !muted.value
      break
    case 'KeyC':
      emit('toggle-chat')
      break
    case 'KeyF':
      toggleFullScreen()
      break
    // Та сама літера, що й у ютубі: I — «картинка в картинці»
    case 'KeyI':
      if (!pipSupported.value) break
      togglePip()
      break
  }
}

// --- Трансляція на ТВ (Chromecast / AirPlay) ---
// Порт із watch/Player.vue. Різниця в тому, що тут ефір: streamType для
// Chromecast — LIVE, і позицію в запиті не передаємо, бо її немає. На ТВ
// іде той самий m3u8 — приставка тягне потік сама, наш плеєр їй не потрібен.
const castSupported = ref(false)
const castActive = ref(false)
const castNoDevices = ref(false)
const castContext = shallowRef(null)

// «Пристроїв не знайдено» — це підпис пункту меню на кілька секунд, а не
// окреме повідомлення: меню в цей момент і так відкрите перед очима
const flashNoDevices = () => {
  castNoDevices.value = true
  setTimeout(() => {
    castNoDevices.value = false
  }, 3000)
}

const initGoogleCast = () => {
  if (!import.meta.client) return
  if (
    typeof window.cast === 'undefined' ||
    typeof window.chrome?.cast === 'undefined'
  )
    return

  const ctx = window.cast.framework.CastContext.getInstance()
  ctx.setOptions({
    receiverApplicationId:
      window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
    autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED
  })
  castContext.value = ctx
  castSupported.value = true

  ctx.addEventListener(
    window.cast.framework.CastContextEventType.CAST_STATE_CHANGED,
    (e) => {
      const s = e.castState
      castActive.value =
        s === window.cast.framework.CastState.CONNECTED ||
        s === window.cast.framework.CastState.CONNECTING
    }
  )
}

// Цей колбек смикає сам SDK, коли завантажиться
if (import.meta.client) {
  window.__onGCastApiAvailable = (isAvailable) => {
    if (isAvailable) initGoogleCast()
  }
}

useHead({
  script: [
    {
      src: 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1',
      async: true,
      defer: true,
      tagPosition: 'bodyClose'
    }
  ]
})

const castToGoogle = () => {
  if (!castContext.value || !props.src) return

  const session = castContext.value.getCurrentSession()
  if (!session) {
    // Сесії ще немає — відкриваємо вибір пристрою, а вантажимо вже після
    // того, як глядач когось вибрав
    castContext.value
      .requestSession()
      .then(() => castToGoogle())
      .catch((e) => {
        if (e !== window.chrome.cast.ErrorCode.CANCEL) flashNoDevices()
      })
    return
  }

  const mediaInfo = new window.chrome.cast.media.MediaInfo(
    props.src,
    'application/x-mpegurl'
  )
  mediaInfo.streamType = window.chrome.cast.media.StreamType.LIVE
  mediaInfo.metadata = new window.chrome.cast.media.GenericMediaMetadata()
  mediaInfo.metadata.title = props.title || 'Прямий ефір'
  mediaInfo.metadata.subtitle = 'Olsior'

  session
    .loadMedia(new window.chrome.cast.media.LoadRequest(mediaInfo))
    .catch((e) => {
      console.warn('Cast loadMedia failed', e)
    })
}

// --- AirPlay (Safari) ---
const initAirPlay = () => {
  const video = videoRef.value
  if (!video) return
  if (typeof video.webkitShowPlaybackTargetPicker !== 'function') return

  castSupported.value = true
  video.addEventListener(
    'webkitcurrentplaybacktargetiswirelesschanged',
    () => {
      castActive.value = !!video.webkitCurrentPlaybackTargetIsWireless
    }
  )
}

const toggleCast = () => {
  if (!import.meta.client) return

  const video = videoRef.value

  // На WebKit виграє AirPlay
  if (video && typeof video.webkitShowPlaybackTargetPicker === 'function') {
    video.webkitShowPlaybackTargetPicker()
    return
  }

  if (castContext.value) {
    castToGoogle()
    return
  }

  // Chrome, у якому SDK ще не піднявся — лишається Remote Playback API
  if (video?.remote) {
    video.remote.prompt().catch((e) => {
      if (e?.name === 'NotAllowedError') flashNoDevices()
      else console.warn('Remote Playback prompt failed', e)
    })
  }
}

// Ефір могли перезапустити (нове посилання) — поки трансляція на ТВ жива,
// перезаряджаємо на ній нове джерело
watch(
  () => props.src,
  () => {
    if (!castActive.value || !castContext.value) return
    if (castContext.value.getCurrentSession()) castToGoogle()
  }
)

const FS_EVENTS = [
  'fullscreenchange',
  'webkitfullscreenchange',
  'mozfullscreenchange',
  'MSFullscreenChange'
]

onMounted(async () => {
  if (!import.meta.client) return

  isMobile.value = window.innerWidth < 1024

  loadVolumeSettings()
  loadShaderSettings()

  try {
    showDebug.value = localStorage.getItem(DEBUG_KEY) === '1'
  } catch (e) {}

  // Шейдер лишається десктопною фішкою — на мобільних GPU це надто дорого.
  // Але вирішує саме тип пристрою, а не ширина вікна: на /live чат забирає
  // чверть екрана, тож у вікні 900-1000px (звичайна справа на ноуті)
  // ширина впала б нижче порогу і апскейл мовчки зникав би з меню.
  const isTouchDevice =
    window.matchMedia?.('(pointer: coarse)').matches ?? isMobile.value
  coarsePointer.value = isTouchDevice
  shaderSupported.value = !isTouchDevice && checkShaderSupport()
  if (shaderSupported.value && shaderMode.value !== 'off') {
    if (await ensureAnime4K()) setupShader()
    else shaderMode.value = 'off'
  }

  const video = videoRef.value
  if (video) {
    video.volume = Number(volume.value) || 0
    video.muted = muted.value
  }

  detectPipSupport()
  initAirPlay()
  // SDK міг встигнути завантажитись раніше за нас — тоді колбека вже не буде
  if (window.cast) initGoogleCast()
  // У Chrome показуємо пункт одразу: SDK доїде асинхронно, а чекати на нього
  // з порожнім меню — гірше, ніж один зайвий пункт
  if (!castSupported.value && window.chrome && !window.safari) {
    castSupported.value = true
  }

  const hlsModule = await import('hls.js')
  Hls = hlsModule.default

  uptimeTimer = setInterval(() => (now.value = Date.now()), 1000)

  window.addEventListener('keydown', keyHandler)
  FS_EVENTS.forEach((evt) =>
    document.addEventListener(evt, handleFullscreenChange)
  )
  document.addEventListener('mousemove', onDocumentMousemove)
  document.addEventListener('mouseup', onDocumentMouseup)
  document.addEventListener('touchmove', onDocumentTouchmove, { passive: true })
  document.addEventListener('touchend', onDocumentTouchend)

  initPlayer()
})

onBeforeUnmount(() => {
  destroyShader()
  destroyHls()
  clearRetry()
  if (uptimeTimer) {
    clearInterval(uptimeTimer)
    uptimeTimer = null
  }
  if (debugTimer) {
    clearInterval(debugTimer)
    debugTimer = null
  }
  clearTimeout(seekCommitTimer)
  if (!import.meta.client) return
  window.removeEventListener('keydown', keyHandler)
  FS_EVENTS.forEach((evt) =>
    document.removeEventListener(evt, handleFullscreenChange)
  )
  document.removeEventListener('mousemove', onDocumentMousemove)
  document.removeEventListener('mouseup', onDocumentMouseup)
  document.removeEventListener('touchmove', onDocumentTouchmove)
  document.removeEventListener('touchend', onDocumentTouchend)
})

// Для редактора кліпу: прев'ю — цей самий <video>, а сегменти беруться з
// цього самого hls.js (див. utils/clip.js)
defineExpose({
  isFullscreen,
  getVideo: () => videoRef.value,
  getHls: () => hlsInstance.value,
  exitFullscreen: async () => {
    if (isFullscreen.value) await toggleFullScreen()
  }
})
</script>

<template>
  <div
    ref="playerRef"
    class="livePlayer relative bg-black outline-none w-full h-full"
    :tabindex="0"
  >
    <div
      ref="compareContainerRef"
      class="relative w-full h-full"
    >
      <video
        ref="videoRef"
        class="block w-full h-full object-contain"
        :class="{ 'opacity-0': shaderActive && !compareMode }"
        crossorigin="anonymous"
        preload="auto"
        playsinline
        x-webkit-airplay="allow"
        @play="playing = true"
        @pause="onPause"
        @waiting="onWaiting"
        @seeking="onSeeking"
        @playing="waiting = false"
        @timeupdate="onTimeUpdate"
        @progress="syncDvr"
        @seeked="syncDvr"
        @loadedmetadata="syncDvr"
        @loadeddata="detectPipSupport"
        @resize="syncNativeActiveLevel"
        @webkitbeginfullscreen="onNativeFullscreen(true)"
        @webkitendfullscreen="onNativeFullscreen(false)"
        @enterpictureinpicture="pipActive = true"
        @leavepictureinpicture="pipActive = false"
        @webkitpresentationmodechanged="onPresentationModeChange"
        @dblclick="onVideoDblclick"
        @volumechange="
          () => {
            if (videoRef) {
              volume = videoRef.volume
              muted = videoRef.muted
            }
          }
        "
      />

      <!--
        Дебаг-панель. pointer-events-none — щоб не перехоплювала кліки по
        відео (пауза, виклик контролів)
      -->
      <div
        v-if="showDebug && debugStats"
        class="top-2 left-2 z-20 absolute bg-black/70 p-2 rounded font-mono text-[11px] text-success leading-tight whitespace-pre pointer-events-none"
      >
        <div>Транспорт: {{ debugStats.transport }}</div>
        <div>Відео: {{ debugStats.res }}</div>
        <div>Рівень: {{ debugStats.level }}</div>
        <div>Авто-якість: {{ debugStats.auto }}</div>
        <div>Відставання: {{ debugStats.behind }}</div>
        <div>Норма затримки: {{ debugStats.tolerance }}</div>
        <div>DVR-вікно: {{ debugStats.dvr }}</div>
        <div>Позиція: {{ debugStats.position }}</div>
        <div>Буфер попереду: {{ debugStats.buffer }}</div>
        <div>Пропущені кадри: {{ debugStats.dropped }}</div>
        <div>Шейдер: {{ debugStats.shader }}</div>
      </div>

      <!-- Прихований канвас: у нього рендерить сама бібліотека anime4k.js -->
      <canvas
        v-if="shaderSupported"
        ref="shaderCanvasRef"
        class="hidden"
      />
      <!-- Видимий канвас апскейлу -->
      <canvas
        v-if="shaderSupported"
        ref="shaderDisplayCanvasRef"
        v-show="shaderActive"
        class="block top-0 left-0 absolute w-full h-full object-contain pointer-events-none"
        :style="
          compareMode && shaderActive
            ? { clipPath: `inset(0 0 0 ${compareDividerPct}%)` }
            : {}
        "
      />

      <!-- Compare-режим: розділювач та підписи -->
      <template v-if="compareMode && shaderActive">
        <div
          class="top-0 z-20 absolute flex justify-center items-center h-full cursor-ew-resize select-none"
          :style="{ left: `calc(${compareDividerPct}% - 1px)`, width: '3px' }"
          @mousedown="onDividerMousedown"
          @touchstart.passive="onDividerTouchstart"
        >
          <div class="bg-white/90 shadow-lg w-[3px] h-full" />
          <div
            class="top-1/2 left-1/2 z-30 absolute flex justify-center items-center bg-white shadow-lg rounded-full w-8 h-8 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M8 5l-5 7 5 7M16 5l5 7-5 7"
                stroke="#333"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </div>
        <div
          class="top-3 left-3 z-20 absolute bg-black/60 px-2 py-0.5 rounded text-white text-xs pointer-events-none select-none"
        >
          Оригінал
        </div>
        <div
          class="top-3 right-3 z-20 absolute bg-black/60 px-2 py-0.5 rounded text-white text-xs pointer-events-none select-none"
        >
          Апскейл
        </div>
      </template>
    </div>

    <!--
      Заставка режиму «тільки звук». Відео під нею є (RTMP без відеодоріжки
      не буває), але це 256×144 — краще чесна плашка, ніж мило на весь екран
    -->
    <div
      v-if="activeLevelAudioOnly && !streamOffline && !failed"
      class="absolute inset-0 flex flex-col justify-center items-center gap-2 bg-black text-base-content/70 pointer-events-none"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
      >
        <path d="M3 14v-2a9 9 0 0 1 18 0v2" />
        <rect x="2" y="13" width="5" height="7" rx="2" fill="currentColor" stroke="none" />
        <rect x="17" y="13" width="5" height="7" rx="2" fill="currentColor" stroke="none" />
      </svg>
      <p class="font-semibold text-sm">Тільки звук</p>
      <p class="text-xs opacity-60">Оберіть якість у налаштуваннях, щоб повернути відео</p>
    </div>

    <!-- Спінер буферизації -->
    <div
      v-if="waiting && !streamOffline && !failed"
      class="absolute inset-0 flex justify-center items-center pointer-events-none"
    >
      <VideoSpinner />
    </div>

    <!-- Стрім ще не почався -->
    <div
      v-if="streamOffline"
      class="absolute inset-0 flex flex-col justify-center items-center gap-3 bg-base-100 px-6 text-center"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 24 24"
        class="opacity-40"
      >
        <path
          fill="currentColor"
          d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2m0 14H4V6h16z"
        />
      </svg>
      <p class="font-semibold text-lg">Стрім зараз офлайн</p>
      <p class="opacity-60 text-sm">
        Перевіримо ще раз через {{ retryIn }} с
      </p>
      <button
        class="btn btn-primary btn-sm"
        @click="
          () => {
            clearRetry()
            initPlayer()
          }
        "
      >
        Перевірити зараз
      </button>
    </div>

    <!-- Непоправна помилка відтворення -->
    <div
      v-else-if="failed"
      class="absolute inset-0 flex flex-col justify-center items-center gap-3 bg-base-100 px-6 text-center"
    >
      <p class="font-semibold text-lg">Не вдалося відтворити стрім</p>
      <button
        class="btn btn-primary btn-sm"
        @click="initPlayer"
      >
        Спробувати ще раз
      </button>
    </div>

    <!-- Панель керування -->
    <div
      v-show="controlsVisible && !streamOffline && !failed"
      class="bottom-0 left-0 absolute bg-neutral bg-opacity-70 px-[10px] lg:px-[20px] py-[10px] lg:py-[16px] w-full transition-all video-menu"
    >
      <!--
        Перемотка по DVR-вікну Mux — окремим рядком над кнопками, на всю
        ширину: у ряду з елементами керування смужка виходила куца, і на
        телефоні за неї нічим було взятись.
        Рядок з'являється лише за кнопкою «Запис»: на прямому ефірі смужка
        більшості не потрібна й лише провокує випадкові відмотки
      -->
      <div
        v-if="dvrAvailable && recordMode"
        class="flex items-center gap-[8px] px-1 pb-[6px] lg:pb-[8px]"
      >
        <VideoScrubber
          v-model="scrubValue"
          :max="dvrLength"
          commit-on-release
          class="flex-1"
          title="Перемотати ефір (стрілки ← →)"
        >
          <!--
            Під курсором показуємо час ефіру в тій точці, а не зміщення від
            початку DVR-вікна: мотають до «десь на 40-й хвилині стріму», а не
            до «плюс 12 хвилин від початку запису»
          -->
          <template #default="{ position, pendingValue }">
            <div
              class="bottom-[9px] absolute px-[5px] rounded font-mono text-sm text-white -translate-x-1/2 transform tooltip tooltip-open tooltip-primary"
              :style="{ left: position }"
              :data-tip="formatDuration(streamTimeAt(dvrStart + pendingValue))"
            ></div>
          </template>
        </VideoScrubber>
      </div>

      <!--
        Без flex-wrap: розпірка нижче має flex-1, і на wrap-розкладці вона
        розтягується на весь рядок, виштовхуючи кожну наступну кнопку на
        новий — контроли ставали стовпчиком. min-w-0 лишаємо, щоб ряд міг
        стискатись і не виїжджав за край вузького екрана
      -->
      <!--
        Проміжок на телефоні зрізаний до 4px: із кнопкою запису ряд перестав
        уміщатись у 375px і фулскрін виїжджав за край
      -->
      <div
        class="flex items-center gap-[4px] sm:gap-[6px] lg:gap-[10px] min-w-0"
      >
        <!-- Play / pause -->
        <button
          class="flex justify-center items-center btn videoBtn"
          :title="playing ? 'Пауза' : 'Відтворити'"
          @click="togglePlay"
        >
          <svg
            v-if="!playing"
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 24 24"
          >
            <path
              fill="currentColor"
              d="M8 6.82v10.36c0 .79.87 1.27 1.54.84l8.14-5.18a1 1 0 0 0 0-1.69L9.54 5.98A.998.998 0 0 0 8 6.82z"
            ></path>
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 24 24"
          >
            <path
              fill="currentColor"
              d="M8 19c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2s-2 .9-2 2v10c0 1.1.9 2 2 2zm6-12v10c0 1.1.9 2 2 2s2-.9 2-2V7c0-1.1-.9-2-2-2s-2 .9-2 2z"
            ></path>
          </svg>
        </button>

        <!--
          Запис: вмикає смужку перемотки й швидкість. Стоїть одразу за play,
          бо це така сама кнопка керування відтворенням, а не налаштування
        -->
        <button
          v-if="dvrAvailable"
          class="btn videoBtn shrink-0"
          :class="{ 'text-primary': recordMode }"
          :title="
            recordMode
              ? 'Сховати перемотку й повернутись у прямий ефір'
              : 'Дивитись запис: перемотка та швидкість'
          "
          @click="toggleRecordMode"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
          >
            <path
              fill="currentColor"
              d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89.07.14L9 12H6a7 7 0 1 1 2.05 4.95l-1.42 1.42A9 9 0 1 0 13 3m-1 5v5l4.28 2.54.72-1.21l-3.5-2.08V8z"
            />
          </svg>
        </button>

        <!-- Індикатор ефіру: тривалість стріму та онлайн -->
        <div class="flex items-center gap-[10px] px-1 shrink-0">
          <!--
            Коли відмотали назад, індикатор стає кнопкою «повернутись у
            прямий ефір» — крапка гасне, і видно, на скільки ми відстали
          -->
          <!--
            Свідомо span, а не button: у панелі діють глобальні стилі кнопок
            (.btn), і справжній <button> тут роздувався на всю ширину,
            виштовхуючи решту контролів за край екрана
          -->
          <span
            class="flex items-center gap-[6px] cursor-pointer"
            :title="
              atLiveEdge
                ? 'Прямий ефір — натисніть, щоб підтягнутись до самого краю'
                : `Відстаємо на ${behindLive} с — натисніть, щоб повернутись у ефір`
            "
            @click="goLive"
          >
            <span class="relative flex w-2 h-2 shrink-0">
              <span
                v-if="streamLive && atLiveEdge"
                class="inline-flex absolute bg-error opacity-75 rounded-full w-full h-full animate-ping"
              ></span>
              <span
                class="inline-flex relative rounded-full w-2 h-2"
                :class="
                  streamLive && atLiveEdge ? 'bg-error' : 'bg-base-content/40'
                "
              ></span>
            </span>
            <!--
              На телефоні підписи ховаємо — панель і так тісна. Крапка
              лишається: вона й без тексту читається як «ефір іде»
            -->
            <span
              class="hidden sm:inline font-semibold text-xs"
              :class="atLiveEdge ? 'text-error' : 'text-base-content/60'"
            >
              LIVE
            </span>
          </span>

          <!--
            Час ефіру. Наживо це просто його тривалість, а в режимі запису —
            ще й позиція, на якій ми зараз («05:12 / 1:20:44»). Рахуємо не
            від краю DVR-вікна, а від початку самого стріму: глядачу важливо,
            на якій хвилині ефіру він сидить, а не наскільки відстав
          -->
          <span
            v-if="totalStreamTime > 0"
            class="font-mono font-bold tabular-nums text-primary text-xs"
            :title="
              recordMode
                ? 'Позиція в ефірі / скільки вже йде стрім'
                : 'Скільки вже йде стрім'
            "
          >
            <!--
              На вузьких екранах лишаємо саму позицію: панель і так тісна, а
              з повним «05:12 / 1:20:44» кнопки праворуч виїжджали за край
            -->
            <template v-if="recordMode">
              <span :class="{ 'text-warning': !atLiveEdge }">
                {{ formatDuration(scrubStreamTime) }}
              </span>
              <span class="hidden sm:inline">
                / {{ formatDuration(totalStreamTime) }}
              </span>
            </template>
            <template v-else>
              {{ formatDuration(totalStreamTime) }}
            </template>
          </span>

        </div>
        <!--
          Лічильники глядачів переїхали у шапку над відео (див. live.vue):
          у панелі вони змагались за місце з кнопками, а нагорі стоять поруч
          із назвою ефіру, де їх і шукають очима
        -->

        <!-- Звук -->
        <!--
          Кнопку звуку показуємо завжди: на /live чат з'їдає ширину вікна,
          і за lg-порогом (як в основному плеєрі) вона зникала б навіть на
          нормальному ноутбуці. Тягунок ховаємо лише на найвужчих екранах.
        -->
        <!-- На телефоні (<640px) звук крутять кнопками корпусу, а місця в
             ряду бракує — ховаємо, як і в плеєрі записів -->
        <button
          class="max-sm:hidden btn videoBtn"
          title="Звук (колесо — гучність)"
          @click="muted = !muted"
          @wheel.prevent="onVolumeWheel"
        >
          <svg
            v-if="muted || volume === 0"
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 24 24"
          >
            <path
              fill="currentColor"
              d="M3.63 3.63a.996.996 0 0 0 0 1.41L7.29 8.7L7 9H4c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1h3l3.29 3.29c.63.63 1.71.18 1.71-.71v-4.17l4.18 4.18c-.49.37-1.02.68-1.6.91c-.36.15-.58.53-.58.92c0 .72.73 1.18 1.39.91c.8-.33 1.55-.77 2.22-1.31l1.34 1.34a.996.996 0 1 0 1.41-1.41L5.05 3.63c-.39-.39-1.02-.39-1.42 0zM19 12c0 .82-.15 1.61-.41 2.34l1.53 1.53c.56-1.17.88-2.48.88-3.87c0-3.83-2.4-7.11-5.78-8.4c-.59-.23-1.22.23-1.22.86v.19c0 .38.25.71.61.85C17.18 6.54 19 9.06 19 12zm-8.71-6.29l-.17.17L12 7.76V6.41c0-.89-1.08-1.33-1.71-.7zM16.5 12A4.5 4.5 0 0 0 14 7.97v1.79l2.48 2.48c.01-.08.02-.16.02-.24z"
            ></path>
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 24 24"
          >
            <path
              fill="currentColor"
              d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"
            ></path>
          </svg>
        </button>
        <!-- На звуці без звуку тягунок ні до чого — ховаємо разом зі звуком -->
        <VideoScrubber
          v-if="!muted"
          v-model="volume"
          :max="1"
          class="hidden md:flex ml-2 w-24 lg:w-32"
          title="Гучність (колесо теж працює)"
          @wheel.prevent="onVolumeWheel"
        />

        <div class="flex-1"></div>

        <!-- Кліп: вирізати момент ефіру (у межах DVR-вікна сервера). На
             телефоні — пунктом у меню налаштувань: у ряду він виштовхував
             фулскрін за край. Без hls.js (Safari, iPhone) кліп з ефіру не
             зробити — сегменти беремо з його шкали часу, тож кнопки там немає -->
        <button
          v-if="!usingNativeHls"
          class="max-sm:hidden btn videoBtn"
          title="Зробити кліп"
          @click="emit('clip')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle
              cx="6"
              cy="6"
              r="3"
            />
            <circle
              cx="6"
              cy="18"
              r="3"
            />
            <path d="M20 4 8.12 15.88M14.47 14.48 20 20M8.12 8.12 12 12" />
          </svg>
        </button>

        <!-- Чат -->
        <button
          class="relative btn videoBtn"
          :class="{ 'text-primary': chatOpen }"
          title="Чат (C)"
          @click="emit('toggle-chat')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 32 32"
          >
            <path
              fill="currentColor"
              d="M17.74 30L16 29l4-7h6a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h9v2H6a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4h20a4 4 0 0 1 4 4v12a4 4 0 0 1-4 4h-4.84Z"
            ></path>
            <path
              fill="currentColor"
              d="M8 10h16v2H8zm0 6h10v2H8z"
            ></path>
          </svg>
          <span
            v-if="chatUnread > 0 && !chatOpen"
            class="-top-1 -right-1 absolute flex justify-center items-center bg-error px-1 rounded-full min-w-[18px] h-[18px] text-[10px] text-error-content"
          >
            {{ chatUnread > 99 ? '99+' : chatUnread }}
          </span>
        </button>

        <!--
          Меню шейдера Anime4K (тільки десктоп + WebGL).
          На вузьких екранах ховаємо ще й розміткою: shaderSupported дивиться
          на тип пристрою, тож у настільному режимі браузера на телефоні
          кнопка лишалась і без користі з'їдала місце в тісній панелі
        -->
        <VideoMenu
          v-if="shaderSupported"
          class="hidden sm:flex flex-col"
        >
          <template #default="{ toggle }">
            <button
              class="btn videoBtn"
              :class="{ 'text-primary': shaderActive }"
              title="Апскейл Anime4K"
              @click="toggle()"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z"
                ></path>
              </svg>
            </button>
          </template>
          <template #menu>
            <div
              class="right-0 bottom-[60px] absolute flex flex-col gap-[10px] bg-neutral shadow-xl p-3 rounded max-w-[calc(100vw-5rem)] select-none"
            >
              <VideoMenuItem>
                <span>Шейдер</span>
                <select
                  v-model="shaderMode"
                  class="bg-base-300 bg-opacity-50 w-[150px] sm:w-[170px] text-neutral-content select-sm select"
                >
                  <option
                    v-for="opt in shaderModeOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
              </VideoMenuItem>

              <VideoMenuItem v-if="shaderMode !== 'off'">
                <span>Якість</span>
                <select
                  v-model="shaderQuality"
                  class="bg-base-300 bg-opacity-50 w-[150px] sm:w-[170px] text-neutral-content select-sm select"
                >
                  <option
                    v-for="opt in shaderQualityOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
              </VideoMenuItem>

              <VideoMenuItem
                v-if="shaderMode !== 'off' && shaderMode !== 'restore'"
              >
                <span>Апскейл</span>
                <select
                  v-model="shaderScale"
                  class="bg-base-300 bg-opacity-50 w-[150px] sm:w-[170px] text-neutral-content select-sm select"
                >
                  <option
                    v-for="opt in shaderScaleOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
              </VideoMenuItem>

              <VideoMenuItem v-if="shaderMode !== 'off'">
                <span>Режим порівняння</span>
                <input
                  v-model="compareMode"
                  class="toggle toggle-primary"
                  type="checkbox"
                />
              </VideoMenuItem>
            </div>
          </template>
        </VideoMenu>

        <!-- Налаштування потоку: якість і затримка -->
        <VideoMenu class="flex flex-col">
          <template #default="{ toggle }">
            <button
              class="gap-1 btn videoBtn"
              title="Налаштування стріму"
              @click="toggle()"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M19.14 12.94c.04-.3.06-.61.06-.94c0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.488.488 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 0 0-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61zM12 15.6A3.6 3.6 0 0 1 8.4 12A3.6 3.6 0 0 1 12 8.4a3.6 3.6 0 0 1 3.6 3.6a3.6 3.6 0 0 1-3.6 3.6"
                />
              </svg>
              <span class="hidden lg:inline text-xs">
                {{ currentQualityLabel }}
              </span>
            </button>
          </template>
          <template #menu="{ close }">
            <div
              class="right-0 bottom-[60px] absolute flex flex-col gap-[10px] bg-neutral shadow-xl p-3 rounded max-w-[calc(100vw-5rem)] select-none"
            >
              <!--
                Список рівнів приходить або від hls.js, або з мастер-плейлиста,
                який ми розбираємо самі для нативного HLS (iPhone, iPad,
                Safari). Ховаємо пункт лише коли рівень справді один
              -->
              <VideoMenuItem
                v-if="!usingNativeHls"
                class="sm:hidden"
                @click="close(); emit('clip')"
              >
                <div class="flex items-center gap-[6px]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <circle
                      cx="6"
                      cy="6"
                      r="3"
                    />
                    <circle
                      cx="6"
                      cy="18"
                      r="3"
                    />
                    <path d="M20 4 8.12 15.88M14.47 14.48 20 20M8.12 8.12 12 12" />
                  </svg>
                  <span>Зробити кліп</span>
                </div>
              </VideoMenuItem>
              <VideoMenuItem v-if="qualityControllable">
                <span>Якість</span>
                <select
                  v-model="qualityValue"
                  class="bg-base-300 bg-opacity-50 w-[150px] sm:w-[170px] text-neutral-content select-sm select"
                >
                  <option value="auto">
                    Авто{{ activeLevelLabel ? ` (${activeLevelLabel})` : '' }}
                  </option>
                  <option
                    v-for="lvl in [...levels].reverse()"
                    :key="lvl.index"
                    :value="`level:${lvl.index}`"
                  >
                    {{ lvl.label }} — {{ Math.round(lvl.bitrate / 1000) }} kbps
                  </option>
                </select>
              </VideoMenuItem>

              <!--
                Тільки на телефоні: на десктопі ширина тягнеться самим
                роздільником між плеєром і чатом, а на тачі його не вхопиш
              -->
              <VideoMenuItem v-if="isMobile && chatOpen">
                <span>Ширина чату</span>
                <select
                  v-model.number="chatWidthProxy"
                  class="bg-base-300 bg-opacity-50 w-[150px] sm:w-[170px] text-neutral-content select-sm select"
                  title="Скільки екрана займає чат"
                >
                  <option
                    v-for="opt in chatWidthOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
              </VideoMenuItem>

              <!--
                Тільки коли дивимось із запису — наживо цей пункт був би
                кнопкою, яка нічого не робить.
                Біля краю ефіру не ховаємо, а глушимо: пункт зникав би просто
                тому, що глядач доїхав до ефіру, і меню під курсором стрибало б
              -->
              <VideoMenuItem v-if="recordMode || !atLiveEdge">
                <span>Швидкість</span>
                <select
                  v-model.number="playbackRate"
                  class="bg-base-300 bg-opacity-50 w-[150px] sm:w-[170px] text-neutral-content select-sm select"
                  :disabled="atLiveEdge"
                  :title="
                    atLiveEdge
                      ? 'Наживо прискорювати нічого: попереду ще немає відео'
                      : 'Прискорити, щоб наздогнати ефір'
                  "
                >
                  <option
                    v-for="rate in PLAYBACK_RATES"
                    :key="rate"
                    :value="rate"
                  >
                    {{ rate }}x
                  </option>
                </select>
              </VideoMenuItem>

              <VideoMenuItem>
                <span>Дебаг</span>
                <input
                  v-model="showDebug"
                  class="toggle toggle-primary"
                  type="checkbox"
                  title="Показати технічні дані потоку"
                />
              </VideoMenuItem>

              <!--
                Картинка в картинці. У панелі керування для неї місця немає:
                на телефоні там і так тісно, а тиснуть її не щохвилини —
                тож живе поруч із трансляцією на ТВ, це сусідні за змістом
                речі: куди подіти вікно з ефіром
              -->
              <VideoMenuItem
                v-if="pipSupported"
                @click="togglePip"
              >
                <div class="flex items-center gap-[6px]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      fill="currentColor"
                      d="M19 11h-8v6h8zm4 8V4.98C23 3.88 22.1 3 21 3H3c-1.1 0-2 .88-2 1.98V19c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2m-2 .02H3V4.97h18z"
                    />
                  </svg>
                  <span :class="{ 'text-primary': pipActive }">
                    {{
                      pipActive
                        ? 'Повернути з окремого вікна'
                        : 'Картинка в картинці'
                    }}
                  </span>
                </div>
              </VideoMenuItem>

              <!--
                Chromecast (або AirPlay на Apple). Меню не закриваємо: далі
                йде системний вибір пристрою, і після нього цей же пункт
                показує, чи трансляція піднялась
              -->
              <VideoMenuItem
                v-if="castSupported"
                @click="toggleCast"
              >
                <div class="flex items-center gap-[6px]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      fill="currentColor"
                      :opacity="castActive ? 1 : 0.85"
                      d="M21 3H3c-1.1 0-2 .9-2 2v3h2V5h18v14h-7v2h7c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM1 18v3h3c0-1.66-1.34-3-3-3zm0-4v2c2.76 0 5 2.24 5 5h2c0-3.87-3.13-7-7-7zm0-4v2c4.97 0 9 4.03 9 9h2c0-6.08-4.93-11-11-11z"
                    />
                    <!-- Крапка, поки трансляція активна -->
                    <circle
                      v-if="castActive"
                      cx="17"
                      cy="12"
                      r="3"
                      fill="currentColor"
                      class="text-primary"
                    />
                  </svg>
                  <span
                    :class="{
                      'text-primary': castActive,
                      'text-warning': castNoDevices
                    }"
                  >
                    {{
                      castActive
                        ? 'Трансляція активна'
                        : castNoDevices
                          ? 'Пристроїв не знайдено'
                          : 'Транслювати на ТВ'
                    }}
                  </span>
                </div>
              </VideoMenuItem>
            </div>
          </template>
        </VideoMenu>

        <!-- Фулскрін -->
        <button
          class="btn videoBtn"
          title="На весь екран (F)"
          @click="toggleFullScreen"
        >
          <svg
            v-if="!isFullscreen"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
          >
            <path
              fill="currentColor"
              d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"
            ></path>
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
          >
            <path
              fill="currentColor"
              d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"
            ></path>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.videoBtn {
  --tw-bg-opacity: 0;
  border: none;
  min-height: 2rem;
  height: 2rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  color: hsla(var(--nc) / 1);
}

.videoBtn:hover {
  --tw-bg-opacity: 0.2;
  background-color: hsla(var(--p) / var(--tw-bg-opacity));
}

.tooltip::before {
  font-size: inherit;
  padding: inherit;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.5);
}
</style>
